import React, { useRef, useState } from 'react'
import { bool, string, object, func } from 'prop-types'
import ReCAPTCHA from 'react-google-recaptcha'

import styles from './EditorialContactForm.module.scss'
import FieldTemplate from '../FieldTemplate'
import TextControl from '../TextControl'
import classNames from 'classnames'
import ButtonStandard from '../ButtonStandard'
import LoadingSpinner from '../Spinner'
import useOutsideClick from '@/hook/useOutsideClick'
import Type from '@/component/Primitive/Type'
import ContactConfirmation from '../ContactConfirmation'

const EditorialContactForm = ({
  successMessage,
  errorMessage,
  errors = {},
  handleOnChange,
  handleOnSubmit,
  setValues,
  setErrors,
  waiting,
  data
}) => {
  const recaptchaRef = React.createRef()
  const onReCAPTCHAChange = (code) => {
    setValues({ captcha: code })
  }
  const onSubmit = (e) => {
    e.preventDefault()
    const recaptchaValue = recaptchaRef.current.getValue()
    if (recaptchaValue) {
      return handleOnSubmit()
    }
    setErrors({ captcha: 'Please complete reCaptcha' })
  }
  return (
    <>
      {successMessage && (
        <Type as="h2" size="heading3" color="green">
          <ContactConfirmation />
        </Type>
      )}
      {errorMessage && (
        <Type as="h2" size="heading3" color="red">
          {errorMessage}
        </Type>
      )}
      {waiting && <LoadingSpinner />}
      {!waiting && !successMessage && (
        <form onSubmit={onSubmit} className={styles.EditorialContactForm}>
          <CustomField
            label="First name"
            name="firstName"
            handleOnChange={handleOnChange}
            error={errors.firstName}
            value={data.firstName}
            required
          />
          <CustomField
            label="Last name"
            name="lastName"
            error={errors.lastName}
            value={data.lastName}
            handleOnChange={handleOnChange}
            required
          />
          <CustomField
            label="Email"
            name="email"
            error={errors.email}
            value={data.email}
            handleOnChange={handleOnChange}
            required
          />
          <CustomField
            label="Phone"
            name="phone"
            error={errors.phone}
            value={data.phone}
            handleOnChange={handleOnChange}
          />
          <CustomField
            label="Message"
            name="message"
            error={errors.message}
            value={data.message}
            handleOnChange={handleOnChange}
            multiLine
            required
          />
          <div className={styles.Captcha}>
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey="6Ldot24dAAAAAOtGyb7-e6uenTEM91w4Mxw4WNnf"
              onChange={onReCAPTCHAChange}
            />
            {errors.captcha && (
              <Type as="h6" size="heading6" color="red">
                {errors.captcha}
              </Type>
            )}
          </div>
          <ButtonStandard
            type="submit"
            name="submit"
            size="default"
            primary
            className={styles.Submit}
          >
            Submit Message
          </ButtonStandard>
        </form>
      )}
    </>
  )
}

EditorialContactForm.propTypes = {
  successMessage: string,
  errorMessage: string,
  errors: object,
  handleOnChange: func,
  handleOnSubmit: func,
  setValues: func,
  setErrors: func,
  data: object,
  waiting: bool
}

export default EditorialContactForm

const CustomField = ({
  label,
  name,
  required,
  handleOnChange,
  error,
  ...other
}) => {
  const ref = useRef()
  const [hidden, setHidden] = useState(false)

  const handleOutsideClick = () => {
    if (ref.current && ref.current.value === '') {
      setHidden(false)
    } else setHidden(true)
  }

  useOutsideClick(ref, hidden && handleOutsideClick)

  return (
    <div className={styles.CustomField}>
      <FieldTemplate
        hideLabel
        label={label}
        required={required}
        controlName={name}
        onClick={() => setHidden(true)}
      >
        <TextControl
          ref={ref}
          inverse
          name={name}
          required={required}
          onChange={handleOnChange}
          {...other}
        />
        <div
          className={classNames(styles.Placeholder, hidden && styles.hidden)}
        >
          {label} <sup>*</sup>
        </div>
      </FieldTemplate>
    </div>
  )
}

CustomField.propTypes = {
  label: string.isRequired,
  name: string.isRequired,
  error: string,
  required: bool,
  handleOnChange: func
}
